import { CommonModule } from '@angular/common';
import {
    Component,
    computed,
    inject,
    input,
    OnInit,
    signal,
} from '@angular/core';
import { MatRippleModule } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import {
    CalendarEvent,
    settingSignal,
    SettingsService,
    Space,
} from '@placeos/common';

import { OrganisationService } from '@placeos/common';
import {
    AuthenticatedImageDirective,
    IconComponent,
    TranslatePipe,
} from '@placeos/components';
import { GroupEventDetailsModalComponent } from './group-event-details-modal.component';
import { SpacePipe } from './space.pipe';

@Component({
    selector: 'group-event-card',
    template: `
        @if (!featured()) {
            <button
                matRipple
                (click)="viewDetails()"
                class="border-base-300 bg-base-100 hover:border-info flex h-80 w-60 flex-col overflow-hidden rounded-xl border shadow-sm hover:shadow-2xl"
            >
                <div
                    class="border-base-200 bg-base-200 relative flex h-28 min-h-28 w-full items-center justify-between overflow-hidden border-b"
                >
                    @if (event().images?.length) {
                        <img
                            auth
                            [source]="event().images[0]"
                            class="absolute top-0 left-0 h-full w-full object-cover object-center"
                        />
                    }
                </div>
                <div class="h-1/2 w-full flex-1 p-4">
                    <div class="text-left text-sm opacity-60">
                        {{ event().date | date: 'EEE d MMM' }},
                        {{ event().date | date: time_format }}
                    </div>
                    <h2
                        class="mb-2 w-full truncate text-left text-xl"
                        [title]="event().title"
                    >
                        {{ event().title }}
                    </h2>
                    <div
                        class="mb-2 h-18 flex-1 overflow-hidden text-left text-xs opacity-60"
                    >
                        <p class="line-clamp-4">{{ raw_description() }}</p>
                        @if (!raw_description().trim()) {
                            <p class="opacity-30">
                                {{
                                    'CALENDAR_EVENT.GROUP_NO_DESCRIPTION'
                                        | translate
                                }}
                            </p>
                        }
                    </div>
                    <div class="flex items-center space-x-2 text-sm">
                        <icon class="text-info">place</icon>
                        @if (is_onsite() && has_space()) {
                            <div>
                                {{ space().display_name || space().name || '' }}
                            </div>
                        }
                        @if (is_onsite() && !has_space()) {
                            <div class="opacity-30">
                                {{
                                    'CALENDAR_EVENT.GROUP_UNCONFIRMED'
                                        | translate
                                }}
                            </div>
                        }
                        @if (!is_onsite()) {
                            <div class="opacity-30">
                                {{ 'CALENDAR_EVENT.GROUP_REMOTE' | translate }}
                            </div>
                        }
                    </div>
                    <div class="flex items-center space-x-2 text-sm">
                        <icon class="text-info">people</icon>
                        <div class="">
                            {{
                                'CALENDAR_EVENT.GROUP_ATTENDING'
                                    | translate
                                        : {
                                              count:
                                                  event().attendees?.length ||
                                                  '0',
                                          }
                            }}
                        </div>
                    </div>
                </div>
            </button>
        } @else {
            <button
                matRipple
                (click)="viewDetails()"
                class="border-base-300 bg-base-100 hover:border-info mx-auto flex h-56 w-252 max-w-full overflow-hidden rounded-xl border shadow-sm hover:shadow-2xl"
            >
                <div
                    class="border-base-200 bg-base-200 relative flex h-full w-1/2 max-w-[18rem] items-center justify-between overflow-hidden border-r"
                >
                    @if (event().images?.length) {
                        <img
                            auth
                            [source]="event().images[0]"
                            class="absolute top-0 left-0 h-full w-full object-cover object-center"
                        />
                    }
                </div>
                <div
                    class="bg-info text-info-content absolute top-0 left-0 flex items-center space-x-2 rounded-br-xl py-2 pr-4 pl-2 text-sm"
                >
                    <icon class="text-base">star</icon>
                    <div class="uppercase">
                        {{ 'CALENDAR_EVENT.GROUP_FEATURED' | translate }}
                    </div>
                </div>
                <div
                    details
                    class="flex w-1/2 max-w-[calc(100%-18rem)] flex-1 space-x-4 overflow-hidden px-4 py-4"
                >
                    <div class="flex flex-col items-center">
                        <div class="text-sm opacity-30">
                            {{ event().date | date: 'MMM' }}
                        </div>
                        <div class="text-lg">
                            {{ event().date | date: 'd' }}
                        </div>
                    </div>
                    <div class="flex w-px flex-1 flex-col space-y-2">
                        <h3 class="text-left">{{ event().title }}</h3>
                        <div time class="text-left text-sm opacity-30">
                            {{ event().date | date: 'EEEE' }}
                            {{ event().date | date: time_format }} -
                            {{
                                event().date + event().duration * 60 * 1000
                                    | date: time_format
                            }}
                        </div>
                        <div class="h-20 overflow-hidden text-left">
                            <p class="line-clamp-3">{{ raw_description() }}</p>
                            @if (!raw_description().trim()) {
                                <p class="opacity-30">
                                    {{
                                        'CALENDAR_EVENT.GROUP_NO_DESCRIPTION'
                                            | translate
                                    }}
                                </p>
                            }
                        </div>
                        <div class="flex items-center space-x-2 text-sm">
                            <icon class="text-info">place</icon>
                            @if (is_onsite() && has_space()) {
                                <div>
                                    {{
                                        space().display_name ||
                                            space().name ||
                                            ''
                                    }}
                                </div>
                            }
                            @if (is_onsite() && !has_space()) {
                                <div class="opacity-30">
                                    {{
                                        'CALENDAR_EVENT.GROUP_UNCONFIRMED'
                                            | translate
                                    }}
                                </div>
                            }
                            @if (!is_onsite()) {
                                <div class="opacity-30">
                                    {{
                                        'CALENDAR_EVENT.GROUP_REMOTE'
                                            | translate
                                    }}
                                </div>
                            }
                        </div>
                        <div class="flex items-center space-x-2 text-sm">
                            <icon class="text-info">people</icon>
                            <div class="">
                                {{
                                    'CALENDAR_EVENT.GROUP_ATTENDING'
                                        | translate
                                            : {
                                                  count:
                                                      attendee_count() || '0',
                                              }
                                }}
                            </div>
                        </div>
                    </div>
                </div>
                <div
                    class="bg-secondary text-secondary-content absolute top-4 right-4 w-32 truncate rounded-sm px-4 py-2 text-center"
                >
                    {{ 'COMMON.VIEW_DETAILS' | translate }}
                </div>
            </button>
        }
    `,
    styles: [
        `
            button {
                transition:
                    box-shadow 300ms,
                    border 200ms;
            }
        `,
    ],
    imports: [
        CommonModule,
        TranslatePipe,
        MatRippleModule,
        RouterModule,
        MatTooltipModule,
        IconComponent,
        AuthenticatedImageDirective,
    ],
})
export class GroupEventCardComponent implements OnInit {
    private _settings = inject(SettingsService);
    private _dialog = inject(MatDialog);
    private _org = inject(OrganisationService);

    public readonly event = input<CalendarEvent>(undefined);
    public readonly featured = input<boolean>(undefined);

    public readonly space = signal<Space>(new Space());
    public readonly raw_description = signal('');
    public readonly is_onsite = computed(
        () => this.event()?.extension_data.attendance_type !== 'ONLINE',
    );
    public readonly has_space = computed(() => !!this.space()?.id);
    public readonly is_online = computed(
        () =>
            !this.is_onsite ||
            this.event()?.extension_data.attendance_type === 'ANY',
    );
    public readonly group_event_calendar = settingSignal(
        'group_events_calendar',
    );
    public readonly attendee_count = computed(
        () =>
            this.event()?.attendees?.filter(
                (user) => user.email !== this.group_event_calendar(),
            )?.length || 0,
    );

    public get time_format(): string {
        return this._settings.time_format;
    }

    public async ngOnInit() {
        const space_pipe = new SpacePipe();
        space_pipe.org = this._org;
        const resource = this.event().resources.find(
            (_) => _.email !== this.group_event_calendar(),
        );
        this.space.set(
            await space_pipe.transform(resource?.id || resource?.email),
        );
        this.raw_description.set(this.removeHtmlTags(this.event().body));
    }

    public removeHtmlTags(html: string) {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        return doc.body.textContent || '';
    }

    public viewDetails(): void {
        this._dialog.open(GroupEventDetailsModalComponent, {
            data: { event: this.event(), concierge: false },
        });
    }
}
